/* ------------------------------------------------------------------------------
 *
 *  # Student Profile Components
 *
 *  Tabs dinámicos y navegación en ficha de estudiante
 *
 * ---------------------------------------------------------------------------- */

// Variables de estado
let studentProfileCallbacks = null;
let currentActiveTab = 'resumen';

// Inicializar tabs del perfil de estudiante
export function initStudentProfileTabs(estudianteId, defaultTab = 'resumen', availableTabs = []) {
	currentActiveTab = defaultTab;
	
	// Get tab elements
	const tabButtons = document.querySelectorAll('.student-tab-btn');
	const tabPanes = document.querySelectorAll('.student-tab-pane');
	
	if (tabButtons.length === 0) {
		console.warn('No tab buttons found');
		return;
	}
	
	// Hide tabs that are not available
	tabButtons.forEach(btn => {
		const tabId = btn.getAttribute('data-tab');
		if (!availableTabs.includes(tabId)) {
			btn.style.display = 'none';
		}
	});
	
	// Set initial active tab
	activateTab(defaultTab);
	
	// Add click handlers
	tabButtons.forEach(btn => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			const tabId = btn.getAttribute('data-tab');
			switchTab(tabId, estudianteId);
		});
	});
	
	// Handle URL query parameter for tab
	const urlParams = new URLSearchParams(window.location.search);
	const urlTab = urlParams.get('tab');
	if (urlTab && availableTabs.includes(urlTab)) {
		switchTab(urlTab, estudianteId);
	}
}

// Cambiar de tab
export function switchTab(tabId, estudianteId) {
	if (currentActiveTab === tabId) return;
	
	// Deactivate current tab
	deactivateTab(currentActiveTab);
	
	// Activate new tab
	activateTab(tabId);
	
	// Update current tab
	currentActiveTab = tabId;
	
	// Update URL without reload
	updateUrlWithTab(tabId);
	
	// Notify Blazor component
	if (studentProfileCallbacks) {
		studentProfileCallbacks.invokeMethodAsync('OnTabChanged', tabId);
	}
	
	// Load tab content if needed
	loadTabContent(tabId, estudianteId);
}

function activateTab(tabId) {
	// Activate button
	const tabBtn = document.querySelector(`.student-tab-btn[data-tab="${tabId}"]`);
	if (tabBtn) {
		tabBtn.classList.add('active');
	}
	
	// Show pane
	const tabPane = document.querySelector(`.student-tab-pane[data-tab="${tabId}"]`);
	if (tabPane) {
		tabPane.classList.add('active', 'show');
	}
}

function deactivateTab(tabId) {
	// Deactivate button
	const tabBtn = document.querySelector(`.student-tab-btn[data-tab="${tabId}"]`);
	if (tabBtn) {
		tabBtn.classList.remove('active');
	}
	
	// Hide pane
	const tabPane = document.querySelector(`.student-tab-pane[data-tab="${tabId}"]`);
	if (tabPane) {
		tabPane.classList.remove('active', 'show');
	}
}

function updateUrlWithTab(tabId) {
	const url = new URL(window.location);
	url.searchParams.set('tab', tabId);
	window.history.pushState({}, '', url);
}

// Cargar contenido del tab
export async function loadTabContent(tabId, estudianteId) {
	const tabPane = document.querySelector(`.student-tab-pane[data-tab="${tabId}"]`);
	if (!tabPane) return;
	
	// Check if already loaded
	if (tabPane.getAttribute('data-loaded') === 'true') {
		return;
	}
	
	// Show loading state
	showTabLoading(tabPane);
	
	try {
		// Notify Blazor to load content
		if (studentProfileCallbacks) {
			await studentProfileCallbacks.invokeMethodAsync('LoadTabContent', tabId, estudianteId);
		}
		
		// Mark as loaded
		tabPane.setAttribute('data-loaded', 'true');
		
		// Hide loading state
		hideTabLoading(tabPane);
		
		// Initialize specific tab functionality
		initTabFunctionality(tabId, estudianteId);
	} catch (error) {
		console.error(`Error loading tab ${tabId}:`, error);
		showTabError(tabPane, 'Error al cargar el contenido');
	}
}

function showTabLoading(tabPane) {
	const loadingHtml = `
		<div class="text-center py-5 tab-loading">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Cargando...</span>
			</div>
			<p class="mt-3 text-muted">Cargando contenido...</p>
		</div>
	`;
	
	const existingLoading = tabPane.querySelector('.tab-loading');
	if (!existingLoading) {
		tabPane.insertAdjacentHTML('beforeend', loadingHtml);
	}
}

function hideTabLoading(tabPane) {
	const loading = tabPane.querySelector('.tab-loading');
	if (loading) {
		loading.remove();
	}
}

function showTabError(tabPane, message) {
	hideTabLoading(tabPane);
	
	const errorHtml = `
		<div class="alert alert-danger d-flex align-items-center" role="alert">
			<i class="ph-warning-circle ph-2x me-3"></i>
			<div>${message}</div>
		</div>
	`;
	
	tabPane.insertAdjacentHTML('beforeend', errorHtml);
}

// Inicializar funcionalidad específica de cada tab
function initTabFunctionality(tabId, estudianteId) {
	switch (tabId) {
		case 'actividades':
			// Initialize activities grid
			if (window.sfBlazorSB.students.activities.initStudentActivitiesGrid) {
				// Grid will be initialized by Blazor component
			}
			break;
			
		case 'observaciones':
			// Initialize observations grid
			if (window.sfBlazorSB.students.observations.initStudentObservationsGrid) {
				// Grid will be initialized by Blazor component
			}
			break;
			
		case 'galeria':
			// Initialize gallery
			if (window.sfBlazorSB.students.works.initStudentWorksGallery) {
				// Gallery will be initialized by Blazor component
			}
			
			// Initialize sub-tabs for gallery
			initGallerySubTabs();
			break;
			
		case 'resumen':
			// Initialize daily summary forms
			initDailySummaryForms();
			break;
	}
}

// Sub-tabs para galería (Propias vs Comunes)
function initGallerySubTabs() {
	const subTabButtons = document.querySelectorAll('.gallery-subtab-btn');
	const subTabPanes = document.querySelectorAll('.gallery-subtab-pane');
	
	if (subTabButtons.length === 0) return;
	
	subTabButtons.forEach(btn => {
		btn.addEventListener('click', (e) => {
			e.preventDefault();
			const subTabId = btn.getAttribute('data-subtab');
			
			// Deactivate all
			subTabButtons.forEach(b => b.classList.remove('active'));
			subTabPanes.forEach(p => p.classList.remove('active', 'show'));
			
			// Activate selected
			btn.classList.add('active');
			const pane = document.querySelector(`.gallery-subtab-pane[data-subtab="${subTabId}"]`);
			if (pane) {
				pane.classList.add('active', 'show');
			}
			
			// Notify callback
			if (studentProfileCallbacks) {
				studentProfileCallbacks.invokeMethodAsync('OnGallerySubTabChanged', subTabId);
			}
		});
	});
}

// Forms del resumen diario (solo para Nido/Comunidad/Casa)
export function initDailySummaryForms() {
	// Initialize date pickers
	const datePickers = document.querySelectorAll('.summary-date-picker');
	datePickers.forEach(picker => {
		if ($(picker).daterangepicker) {
			$(picker).daterangepicker({
				singleDatePicker: true,
				showDropdowns: true,
				locale: {
					format: 'DD/MM/YYYY'
				}
			});
		}
	});
	
	// Initialize time pickers
	const timePickers = document.querySelectorAll('.summary-time-picker');
	timePickers.forEach(picker => {
		if ($(picker).timepicker) {
			$(picker).timepicker({
				timeFormat: 'HH:mm',
				interval: 30,
				dynamic: false,
				dropdown: true,
				scrollbar: true
			});
		}
	});
	
	// Auto-save handlers
	const summaryInputs = document.querySelectorAll('.summary-input');
	summaryInputs.forEach(input => {
		input.addEventListener('change', () => {
			autoSaveSummary();
		});
	});
}

function autoSaveSummary() {
	// Collect form data
	const formData = {};
	
	document.querySelectorAll('.summary-input').forEach(input => {
		const fieldName = input.getAttribute('data-field');
		if (fieldName) {
			if (input.type === 'checkbox') {
				formData[fieldName] = input.checked;
			} else {
				formData[fieldName] = input.value;
			}
		}
	});
	
	// Notify Blazor
	if (studentProfileCallbacks) {
		studentProfileCallbacks.invokeMethodAsync('SaveDailySummary', formData);
	}
	
	// Show save indicator
	showSaveIndicator();
}

function showSaveIndicator() {
	const indicator = document.getElementById('autoSaveIndicator');
	if (indicator) {
		indicator.style.display = 'inline-block';
		indicator.textContent = 'Guardando...';
		
		setTimeout(() => {
			indicator.textContent = 'Guardado ✓';
			setTimeout(() => {
				indicator.style.display = 'none';
			}, 2000);
		}, 500);
	}
}

// Callbacks
export function setStudentProfileCallbacks(dotNetRef) {
	studentProfileCallbacks = dotNetRef;
}

// Navegación desde breadcrumbs o botones
export function navigateToTab(tabId) {
	const tabBtn = document.querySelector(`.student-tab-btn[data-tab="${tabId}"]`);
	if (tabBtn) {
		tabBtn.click();
	}
}

// Get current active tab
export function getCurrentTab() {
	return currentActiveTab;
}

// Refresh tab content
export function refreshTabContent(tabId, estudianteId) {
	const tabPane = document.querySelector(`.student-tab-pane[data-tab="${tabId}"]`);
	if (tabPane) {
		tabPane.setAttribute('data-loaded', 'false');
		loadTabContent(tabId, estudianteId);
	}
}

// Handle tab visibility based on ambiente
export function updateTabVisibility(ambiente) {
	const resumenTab = document.querySelector('.student-tab-btn[data-tab="resumen"]');
	
	if (resumenTab) {
		// Solo mostrar tab "Resumen" para Nido, Comunidad y Casa de Niños
		const showResumen = ['Nido', 'Comunidad', 'Casa de Niños', 'C. Niños'].includes(ambiente);
		resumenTab.style.display = showResumen ? 'block' : 'none';
		
		// Si el tab activo es "Resumen" y no debería mostrarse, cambiar a "Actividades"
		if (!showResumen && currentActiveTab === 'resumen') {
			switchTab('actividades');
		}
	}
}

// Initialize student profile cover image upload
export function initStudentCoverUpload(estudianteId) {
	const uploadBtn = document.getElementById('uploadCoverBtn');
	const coverInput = document.getElementById('coverImageInput');
	
	if (!uploadBtn || !coverInput) return;
	
	uploadBtn.addEventListener('click', () => {
		coverInput.click();
	});
	
	coverInput.addEventListener('change', async (e) => {
		const file = e.target.files[0];
		if (!file) return;
		
		// Validate file type
		if (!file.type.startsWith('image/')) {
			window.showNotification('Por favor selecciona un archivo de imagen', 'error');
			return;
		}
		
		// Show loading
		uploadBtn.disabled = true;
		uploadBtn.innerHTML = '<i class="ph-spinner ph-spin me-2"></i>Subiendo...';
		
		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('estudianteId', estudianteId);
			
			const response = await fetch(`/api/estudiantes/${estudianteId}/cover`, {
				method: 'POST',
				body: formData
			});
			
			if (response.ok) {
				const result = await response.json();
				
				// Update cover image
				const coverImg = document.querySelector('.student-cover-img');
				if (coverImg) {
					coverImg.style.backgroundImage = `url('${result.imageUrl}')`;
				}
				
				window.showNotification('Imagen de portada actualizada', 'success');
				
				// Notify Blazor
				if (studentProfileCallbacks) {
					studentProfileCallbacks.invokeMethodAsync('OnCoverImageUpdated', result.imageUrl);
				}
			} else {
				throw new Error('Upload failed');
			}
		} catch (error) {
			console.error('Error uploading cover:', error);
			window.showNotification('Error al subir la imagen', 'error');
		} finally {
			uploadBtn.disabled = false;
			uploadBtn.innerHTML = '<i class="ph-pencil me-2"></i>Editar cover';
		}
	});
}
